import React from "react";
import { Users, Heart, Globe, Mail } from "lucide-react";

/**
 * Equality, Diversity & Inclusion statement — required for UK CIO
 * application and central to ABGN-UK's Ambedkarite ethos. Babasaheb
 * Ambedkar's life work was opposing discrimination, so this is not a
 * boilerplate statement; it is the founding spirit of the organisation.
 */

const COMMITMENTS = [
  {
    title: "We do not discriminate",
    body: "ABGN-UK welcomes every person regardless of caste, race, ethnicity, nationality, gender, gender identity, sexual orientation, religion or belief, age, disability, neurodiversity, marital status, pregnancy, socio-economic background or political affiliation.",
  },
  {
    title: "We oppose caste discrimination",
    body: "Inspired by Babasaheb Dr. B. R. Ambedkar's lifelong work against caste injustice, ABGN-UK does not record, recognise or respect caste-based hierarchies of any kind. Caste-based discrimination, slurs, jokes or exclusion are not tolerated in any ABGN-UK space, online or in person.",
  },
  {
    title: "We celebrate women's leadership",
    body: "Following Babasaheb's measurement of progress by the progress women have achieved, ABGN-UK actively promotes women into governance, teaching and volunteer-leadership roles. We provide women-only meditation circles where requested, child-friendly meeting times, and zero tolerance for harassment.",
  },
  {
    title: "We make our content accessible",
    body: "Our newsletter and platform support 11 languages with auto-translation. We provide an elder-readable mode (large fonts, high contrast). Live events offer captions where feasible. We design for screen readers and keyboard navigation. Where physical barriers exist at in-person venues, we will work with attendees to find accessible alternatives.",
  },
  {
    title: "We protect the youth and vulnerable",
    body: "All ABGN-UK activities are governed by our Safeguarding Policy. We commission DBS checks for volunteers in regular contact with minors. We will not platform content that glorifies violence, hate, or contradicts our principles.",
  },
  {
    title: "We learn, listen and act",
    body: "Members can report any concern in confidence to info@ambedkaritebuddhistglobalnetworkuk.com. The volunteer governance committee will investigate within 14 days. Where appropriate, we will publicly account for the outcome (anonymised). We do not retaliate against good-faith reporters.",
  },
];

export default function EqualityDiversity() {
  return (
    <div className="bg-pearl">
      <section className="bg-gradient-to-b from-navy via-navy to-navy-light text-pearl">
        <div className="section-container py-16 sm:py-20">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-saffron/15 border border-saffron/40 text-saffron mb-6">
            <Users size={24} />
          </div>
          <p className="eyebrow text-saffron">Statement</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl">
            Equality, diversity and inclusion.
          </h1>
          <blockquote className="mt-8 max-w-3xl border-l-4 border-saffron pl-5 italic font-display text-xl sm:text-2xl text-pearl/95 leading-snug">
            "I measure the progress of a community by the degree of progress which women have achieved."
            <span className="block mt-2 not-italic text-sm text-pearl/55">— Dr. B. R. Ambedkar</span>
          </blockquote>
          <p className="mt-8 text-pearl/80 max-w-3xl leading-relaxed">
            For ABGN-UK, equality is not a clause; it is the reason we exist. Babasaheb's
            life work was the dismantling of caste, gender and economic hierarchies, and
            we carry that mission into every gathering, conversation and digital space.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="section-container max-w-4xl">
          <h2 className="font-display text-3xl text-navy">Our six commitments</h2>
          <p className="mt-3 text-slate-700">
            Written into our governance documents and reviewed annually.
          </p>
          <div className="mt-10 space-y-6">
            {COMMITMENTS.map((c, i) => (
              <div
                key={c.title}
                className="bg-white border border-navy/10 rounded-sm p-7 shadow-sm"
                data-testid={`eandd-${i}`}
              >
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-3xl text-saffron">{i + 1}</span>
                  <h3 className="font-display text-xl sm:text-2xl text-navy">{c.title}</h3>
                </div>
                <p className="mt-3 text-slate-700 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 bg-saffron/10 border border-saffron/30 rounded-sm p-6 flex items-start gap-4">
            <Heart size={22} className="text-saffron mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-display text-xl text-navy">Reporting and contact</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                If you experience or witness discrimination, harassment or any breach of
                this statement at any ABGN-UK activity, please contact us in confidence.
                The volunteer governance committee will respond within 14 days.
              </p>
              <a
                href="mailto:info@ambedkaritebuddhistglobalnetworkuk.com?subject=Equality%20%26%20Diversity"
                data-testid="eandd-email"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-navy text-pearl rounded-sm text-sm hover:bg-navy-light"
              >
                <Mail size={14} /> Email the governance committee
              </a>
            </div>
          </div>

          <p className="mt-12 text-xs text-slate-500 italic">
            Approved February 2026 · Inspired by Babasaheb Dr. B. R. Ambedkar's life and writings · Reviewed annually.
          </p>
        </div>
      </section>
    </div>
  );
}
